import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export type ToastTone = 'error' | 'warning' | 'success' | 'info'

export interface ToastItem {
  id: string
  tone: ToastTone
  title: string
  message?: string
}

const ICONS: Record<ToastTone, ReactNode> = {
  error: (
    <svg className="h-4 w-4 shrink-0 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" />
    </svg>
  ),
  warning: (
    <svg className="h-4 w-4 shrink-0 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4M12 17h.01" />
    </svg>
  ),
  success: (
    <svg className="h-4 w-4 shrink-0 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" />
    </svg>
  ),
  info: (
    <svg className="h-4 w-4 shrink-0 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
    </svg>
  ),
}

const BORDER: Record<ToastTone, string> = {
  error:   'border-red-200   bg-red-50',
  warning: 'border-amber-200 bg-amber-50',
  success: 'border-emerald-200 bg-emerald-50',
  info:    'border-blue-200  bg-blue-50',
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: (id: string) => void }) {
  const ref = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    ref.current = setTimeout(() => onDismiss(item.id), 5000)
    return () => { if (ref.current) clearTimeout(ref.current) }
  }, [item.id, onDismiss])

  return (
    <div className={`flex w-80 items-start gap-3 rounded-xl border px-4 py-3 shadow-lg ${BORDER[item.tone]}`}>
      <div className="mt-0.5">{ICONS[item.tone]}</div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900">{item.title}</p>
        {item.message && <p className="mt-0.5 text-xs text-slate-600">{item.message}</p>}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(item.id)}
        className="shrink-0 text-slate-400 hover:text-slate-600"
        aria-label="Dismiss"
      >
        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}

export function ToastContainer({ toasts, onDismiss }: { toasts: ToastItem[]; onDismiss: (id: string) => void }) {
  if (toasts.length === 0) return null
  return createPortal(
    <div className="fixed bottom-5 right-5 z-50 flex flex-col gap-2.5">
      {toasts.map((t) => <ToastCard key={t.id} item={t} onDismiss={onDismiss} />)}
    </div>,
    document.body,
  )
}
